import React, {ReactNode, useEffect, useState} from 'react';
import AccessDenied from "./AccessDenied";
import FullHeightLoader from "../FullHeightLoader";
import {useCurrentUser} from "@repo/utils";
import {isDevEnv} from "@repo/services";

type RoleWrapperProps = {
    checkFunction: (userRoles: string[] | undefined) => boolean | undefined;
    children: ReactNode;
};

/**
 * Gates rendering of its children behind a caller-supplied role/permission check, evaluating the current user's roles and permission keys against `checkFunction` and showing a loader until the check resolves.
 *
 * Legacy component — prefer `SecuredRoute`/`hasRequiredPermissions` for new permission gating; this checks both `userRoles` and `userPermissionKeys` for backward compatibility.
 */
const RoleWrapper = ({ checkFunction, children }: RoleWrapperProps) => {
    const [isReady, setIsReady] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const { userRoles, userPermissionKeys } = useCurrentUser();

    useEffect(() => {
        if (userRoles) {
            const rolesResult = checkFunction(userRoles);
            const permissionsResult = checkFunction(userPermissionKeys);
            const access = rolesResult || permissionsResult || false;

            if (isDevEnv) {
                console.group('[RoleWrapper] Access check');
                console.log('checkFunction:', checkFunction.name || checkFunction.toString().slice(0, 80));
                console.log('userRoles:', userRoles);
                console.log('userPermissionKeys:', userPermissionKeys);
                console.log('result (roles):', rolesResult);
                console.log('result (permissions):', permissionsResult);
                console.log('access granted:', access);
                console.groupEnd();
            }

            setHasAccess(access);
            setIsReady(true);
        }
    }, [userRoles, userPermissionKeys]);

    if (!isReady) {
        return <FullHeightLoader message={"Fetching Data"} />; // Placeholder while processing roles
    }

    if (!hasAccess) {
        return <AccessDenied />;
    }

    return <>{children}</>;
};

export default RoleWrapper;
